"use client";

import type * as React from "react";
import {
  FilterPill,
  FilterPillGroup,
  FilterSection,
} from "@/components/booking/filter/FilterPill";
import {
  fuelTypeIcons,
  transmissionIcons,
  vehicleCategoryIcons,
} from "@/lib/master-data";
import { useAppSelector } from "@/lib/redux/hooks";
import {
  selectMasterData,
  selectMasterDataError,
  selectMasterDataStatus,
} from "@/lib/redux/features/master-data/masterDataSlice";
import type { VehicleFilters } from "@/lib/vehicles/filters";

type VehicleFilterFormProps = {
  filters: VehicleFilters;
  onChange: (filters: VehicleFilters) => void;
};

export function VehicleFilterForm({
  filters,
  onChange,
}: VehicleFilterFormProps) {
  const masterData = useAppSelector(selectMasterData);
  const status = useAppSelector(selectMasterDataStatus);
  const error = useAppSelector(selectMasterDataError);

  const toggleCategory = (id: number) => {
    onChange({
      ...filters,
      categoryIds: toggleInList(filters.categoryIds, id),
    });
  };

  const toggleFuel = (id: number) => {
    onChange({
      ...filters,
      fuelTypeIds: toggleInList(filters.fuelTypeIds, id),
    });
  };

  const toggleTransmission = (id: number) => {
    onChange({
      ...filters,
      transmissionIds: toggleInList(filters.transmissionIds, id),
    });
  };

  const setMinPassengers = (value: number) => {
    onChange({
      ...filters,
      minPassengers: filters.minPassengers === value ? null : value,
    });
  };

  if (status === "loading" || status === "idle") {
    return (
      <p className="text-sm text-muted-foreground">Loading filter options…</p>
    );
  }

  if (status === "failed" || !masterData) {
    return (
      <p className="text-sm text-destructive">
        {error ?? "Unable to load filter options."}
      </p>
    );
  }

  return (
    <div className="space-y-8 px-4">
      <FilterSection title="Vehicle type">
        <FilterPillGroup>
          {masterData.vehicle_categories.map((category) => (
            <FilterPill
              key={category.id}
              label={category.name}
              iconClassName={
                vehicleCategoryIcons[category.id] ?? "bi-car-front"
              }
              selected={filters.categoryIds.includes(category.id)}
              onClick={() => toggleCategory(category.id)}
            />
          ))}
        </FilterPillGroup>
      </FilterSection>

      <FilterSection title="Features">
        <FilterPillGroup>
          {masterData.transmissions.map((transmission) => (
            <FilterPill
              key={`transmission-${transmission.id}`}
              label={transmission.name}
              icon={
                transmission.id === 1 ? (
                  <span
                    className="flex size-5 shrink-0 items-center justify-center rounded-full border border-current text-xs font-bold leading-none"
                    aria-hidden="true"
                  >
                    A
                  </span>
                ) : undefined
              }
              iconClassName={
                transmission.id === 2
                  ? transmissionIcons[transmission.id]
                  : undefined
              }
              selected={filters.transmissionIds.includes(transmission.id)}
              onClick={() => toggleTransmission(transmission.id)}
            />
          ))}
          {masterData.fuel_types.map((fuel) => (
            <FilterPill
              key={`fuel-${fuel.id}`}
              label={fuel.name}
              iconClassName={fuelTypeIcons[fuel.id] ?? "bi-fuel-pump"}
              selected={filters.fuelTypeIds.includes(fuel.id)}
              onClick={() => toggleFuel(fuel.id)}
            />
          ))}
        </FilterPillGroup>
      </FilterSection>

      <FilterSection title="Minimum number of seats">
        <FilterPillGroup>
          {masterData.number_of_passengers.map((option) => (
            <FilterPill
              key={option.value}
              label={String(option.value)}
              selected={filters.minPassengers === option.value}
              onClick={() => setMinPassengers(option.value)}
              className="min-w-11 justify-center px-4"
            />
          ))}
        </FilterPillGroup>
      </FilterSection>
    </div>
  );
}

function toggleInList(list: number[], value: number): number[] {
  return list.includes(value)
    ? list.filter((item) => item !== value)
    : [...list, value];
}
